import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Brand, DateFilter, ID, PremiumService, PresetFilter } from "@App/types";
import { SortOrderType } from "@App/types/enums";
import { getGarageListSettings } from "@App/utils/organizationSettings";

export interface FilterReducerState {
  distance: number;
  search: string | null;
  dates: DateFilter | null;
  premiumServices: PremiumService[];
  brand: Brand | null;
  sortBy: SortOrderType;
  bevSupport: boolean;
  commercialVehicleSupport: boolean;
  modalDismissed: boolean;
  onlyBookable: boolean;
  onlyManufacturer: boolean;
  showAsNonBookable: boolean;
  presetFilters: PresetFilter[];
  garageProperties: ID[];
  garageTypeIds: ID[];
}

const FALLBACK_SORTING = SortOrderType.SMALLEST_DISTANCE;
const FALLBACK_RADIUS_IN_METERS = 30000;
const METERS_PER_KILOMETER = 1000;

const getInitialSorting = (): SortOrderType => {
  const { default_sorting: defaultSorting } = getGarageListSettings();

  return Object.values(SortOrderType).includes(defaultSorting as SortOrderType)
    ? (defaultSorting as SortOrderType)
    : FALLBACK_SORTING;
};

/**
 * The organization config holds the radius in kilometers, while the filter state
 * and the garage search API work in meters.
 */
const getInitialRadiusInMeters = (): number => {
  const { default_radius_km: defaultRadiusKm } = getGarageListSettings();

  return typeof defaultRadiusKm === "number" && defaultRadiusKm > 0
    ? defaultRadiusKm * METERS_PER_KILOMETER
    : FALLBACK_RADIUS_IN_METERS;
};

const initialState: FilterReducerState = {
  distance: getInitialRadiusInMeters(),
  search: null,
  dates: null,
  brand: null,
  premiumServices: [],
  sortBy: getInitialSorting(),
  bevSupport: false,
  commercialVehicleSupport: false,
  modalDismissed: false,
  onlyBookable: false,
  onlyManufacturer: false,
  showAsNonBookable: false,
  presetFilters: [],
  garageProperties: [],
  garageTypeIds: [],
};

export const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    // modal actions
    dismissModal(state, action: PayloadAction<boolean>) {
      state.modalDismissed = action.payload;
    },
    // filter actions
    changeDatesFilter(state, action: PayloadAction<DateFilter | null>) {
      state.dates = action.payload;
    },
    changeDistanceFilter(state, action: PayloadAction<number>) {
      state.distance = action.payload;
    },
    changeNameFilter(state, action: PayloadAction<string | null>) {
      state.search = action.payload;
    },
    changeSortByFilter(state, action: PayloadAction<SortOrderType>) {
      state.sortBy = action.payload;
    },
    changeBrandFilter(state, action: PayloadAction<Brand | null>) {
      state.brand = action.payload;
    },
    changeBevSupportFilter(state, action: PayloadAction<boolean>) {
      state.bevSupport = action.payload;
    },
    changeCommercialVehicleSupportFilter(state, action: PayloadAction<boolean>) {
      state.commercialVehicleSupport = action.payload;
    },
    changeBookableFilter(state, action: PayloadAction<boolean>) {
      state.onlyBookable = action.payload;
    },
    changeOnlyManufacturerFilter(state, action: PayloadAction<boolean>) {
      state.onlyManufacturer = action.payload;
    },
    changeShowAsNonBookable(state, action: PayloadAction<boolean>) {
      state.showAsNonBookable = action.payload;
    },
    setPresetFilter(state, action: PayloadAction<PresetFilter[]>) {
      state.presetFilters = action.payload;
    },
    setGarageProperties(state, action: PayloadAction<ID[]>) {
      state.garageProperties = action.payload;
    },
    setGarageTypeIds(state, action: PayloadAction<ID[]>) {
      state.garageTypeIds = action.payload;
    },
  },
});

export const {
  dismissModal,
  changeDatesFilter,
  changeDistanceFilter,
  changeNameFilter,
  changeSortByFilter,
  changeBrandFilter,
  changeBevSupportFilter,
  changeCommercialVehicleSupportFilter,
  changeBookableFilter,
  changeOnlyManufacturerFilter,
  changeShowAsNonBookable,
  setPresetFilter,
  setGarageProperties,
  setGarageTypeIds,
} = filterSlice.actions;

export default filterSlice.reducer;
